import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Check, Loader2, List } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";

interface OpportunitySourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onSourceCreated?: () => void;
}

export function OpportunitySourcesModal({
  isOpen,
  onClose,
  slug,
  onSourceCreated,
}: OpportunitySourcesModalProps) {
  const { toast } = useToast();
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    sources: false,
    create: false,
    update: false,
    delete: false,
  });
  const [editingSource, setEditingSource] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    is_default: false,
    is_active: true,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && slug) {
      fetchSources();
    }
  }, [isOpen, slug]);

  useEffect(() => {
    if (editingSource && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 50);
    }
  }, [editingSource]);

  const fetchSources = async () => {
    try {
      setLoading((prev) => ({ ...prev, sources: true }));

      const response = await opportunitySourcesAPI.getSources({
        slug,
        is_active: true,
      });

      const sortedSources = response.data.sort((a, b) => {
        if (a.is_default !== b.is_default) {
          return b.is_default ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });

      setSources(sortedSources);
    } catch (error: any) {
      toast({
        title: "Error al cargar fuentes",
        description: error.message || "No se pudieron cargar las fuentes",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, sources: false }));
    }
  };

  const handleResetForm = () => {
    setFormData({
      name: "",
      is_default: false,
      is_active: true,
    });
    setEditingSource(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "El nombre es obligatorio",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingSource) {
        setLoading((prev) => ({ ...prev, update: true }));

        if (formData.is_default) {
          await opportunitySourcesAPI.setDefaultSource(editingSource.id, slug);
        } else {
          await opportunitySourcesAPI.updateSource(editingSource.id, {
            name: formData.name.trim(),
            is_default: formData.is_default,
            is_active: formData.is_active,
            slug,
          });
        }

        toast({
          title: "✅ Fuente actualizada",
          description: "La fuente se ha actualizado correctamente",
        });
      } else {
        setLoading((prev) => ({ ...prev, create: true }));

        const sourceData = {
          slug,
          name: formData.name.trim(),
          is_default: formData.is_default,
          is_active: formData.is_active,
        };

        await opportunitySourcesAPI.createSource(sourceData);

        toast({
          title: "✅ Fuente creada",
          description: "La fuente se ha creado correctamente",
        });
      }

      handleResetForm();

      setTimeout(() => {
        fetchSources();
      }, 500);

      onSourceCreated?.();
    } catch (error: any) {
      toast({
        title: editingSource ? "Error al actualizar" : "Error al crear",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });

      setTimeout(() => {
        fetchSources();
      }, 500);
    } finally {
      setLoading((prev) => ({
        ...prev,
        create: false,
        update: false,
      }));
    }
  };

  const handleEdit = (source: any) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      is_default: source.is_default,
      is_active: source.is_active,
    });
  };

  const handleDeleteClick = (id: string) => {
    setSourceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sourceToDelete) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await opportunitySourcesAPI.deleteSource(sourceToDelete, slug);

      toast({
        title: "✅ Fuente eliminada",
        description: "La fuente se ha eliminado correctamente",
      });

      setTimeout(() => {
        fetchSources();
      }, 300);
    } catch (error: any) {
      toast({
        title: "Error al eliminar",
        description: error.message || "No se pudo eliminar la fuente",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setDeleteDialogOpen(false);
      setSourceToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSourceToDelete(null);
  };

  const handleSetDefault = async (id: string) => {
    try {
      setLoading((prev) => ({ ...prev, update: true }));

      const result = await opportunitySourcesAPI.setDefaultSource(id, slug);

      toast({
        title: "✅ Fuente predeterminada",
        description: "La fuente se ha establecido como predeterminada",
      });

      setTimeout(() => {
        fetchSources();
      }, 300);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.message || "No se pudo establecer como predeterminada",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">

          {/* Header */}
          <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex-shrink-0">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                    <List className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                      Gestión de Fuentes
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      Configura las fuentes de donde provienen tus oportunidades
                    </DialogDescription>
                  </div>
                </div>
                {loading.sources && (
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                )}
              </div>
            </DialogHeader>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50 space-y-6">

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white border border-slate-200/80 rounded-[20px] shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
                <div className="h-2.5 w-2.5 rounded-full bg-indigo-500" />
                <h3 className="text-sm font-bold text-slate-800 tracking-tight">
                  {editingSource ? "Editar fuente" : "Nueva fuente"}
                </h3>
              </div>
              <div className="p-5 space-y-4">
                {/* Nombre */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Nombre de la fuente <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="name"
                      ref={nameInputRef}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: Facebook, LinkedIn, Web Site..."
                      className="h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                      disabled={loading.create || loading.update}
                    />
                    <List className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </div>

                {/* Switches */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Predeterminada</p>
                      <p className="text-xs text-slate-400 mt-0.5">Seleccionada por defecto</p>
                    </div>
                    <Switch
                      checked={formData.is_default}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                      disabled={loading.create || loading.update}
                    />
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Activa</p>
                      <p className="text-xs text-slate-400 mt-0.5">Visible en el formulario</p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      disabled={loading.create || loading.update}
                    />
                  </div>
                </div>

                {/* Form buttons */}
                <div className="flex justify-end gap-3 pt-1">
                  {editingSource && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResetForm}
                      disabled={loading.create || loading.update}
                      className="h-9 px-4 rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 text-sm"
                    >
                      Cancelar
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={!formData.name.trim() || loading.create || loading.update}
                    className="h-9 px-5 rounded-lg bg-slate-900 hover:bg-black text-white text-sm font-medium shadow-sm shadow-slate-900/20"
                  >
                    {loading.create || loading.update ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : editingSource ? (
                      <Edit className="h-4 w-4 mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    {editingSource ? "Actualizar Fuente" : "Crear Fuente"}
                  </Button>
                </div>
              </div>
            </form>

            {/* Sources list */}
            <div className="bg-white border border-slate-200/80 rounded-[20px] shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                  <h3 className="text-sm font-bold text-slate-800 tracking-tight">Fuentes existentes</h3>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                  {sources.length} fuente{sources.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="p-4">
                {loading.sources ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
                  </div>
                ) : sources.length === 0 ? (
                  <div className="text-center py-10">
                    <List className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400 font-medium">No hay fuentes configuradas</p>
                    <p className="text-xs text-slate-300 mt-1">Crea la primera usando el formulario</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sources.map((source) => (
                      <div
                        key={source.id}
                        className="group flex items-center justify-between rounded-xl bg-slate-50/80 border border-slate-100 p-3 hover:bg-white hover:shadow-sm hover:border-slate-200 transition-all duration-200"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-xl bg-indigo-100/60 flex items-center justify-center flex-shrink-0">
                            <List className="h-4 w-4 text-indigo-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-slate-800 truncate">{source.name}</p>
                            {source.is_default && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full mt-0.5">
                                <Check className="h-2.5 w-2.5" />
                                Predeterminada
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          {!source.is_default && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetDefault(source.id)}
                              disabled={loading.update || loading.delete || loading.create}
                              className="h-8 px-3 text-xs rounded-lg text-slate-500 hover:bg-emerald-50 hover:text-emerald-700 border border-transparent hover:border-emerald-200"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Predeterminar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(source)}
                            disabled={loading.update || loading.delete || loading.create}
                            className="h-8 w-8 p-0 rounded-lg text-slate-400 hover:bg-indigo-50 hover:text-indigo-600"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClick(source.id)}
                            disabled={loading.delete || loading.update || loading.create}
                            className="h-8 w-8 p-0 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10">
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading.create || loading.update || loading.delete}
                className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Cerrar
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={handleDeleteCancel}>
        <AlertDialogContent className="rounded-[20px] border-slate-100 p-0 overflow-hidden">
          <div className="bg-white border-b border-slate-100 px-6 py-4">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-base font-bold text-slate-900">
                ¿Eliminar esta fuente?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-xs text-slate-500 mt-1">
                Esta acción no se puede deshacer. La fuente se eliminará permanentemente y ya no estará disponible para nuevas oportunidades.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <div className="bg-slate-50/50 px-6 py-4">
            <AlertDialogFooter className="gap-3">
              <AlertDialogCancel
                onClick={handleDeleteCancel}
                disabled={loading.delete}
                className="h-10 px-6 rounded-lg border-slate-200 text-slate-700 font-medium"
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={loading.delete}
                className="h-10 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                {loading.delete ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  "Eliminar"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
